import React, { useEffect, useState, useRef } from 'react'
import styles from './ProfessionalSkillsSection.module.css'
import { getFeaturedTechnologies } from '../../../data/professionalSkills'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'
import { useScrollProgress } from '../../../hooks/useParallax'

// Individual skill card with animated progress and count-up
const SkillCard = ({ tech, index, isInView }) => {
  const [count, setCount] = useState(0)
  const [progressWidth, setProgressWidth] = useState(0)
  const hasAnimated = useRef(false)

  useEffect(() => {
    if (isInView && !hasAnimated.current) {
      hasAnimated.current = true

      // Animate after stagger delay
      const delay = index * 100
      const duration = 1500

      setTimeout(() => {
        const startTime = Date.now()

        const animate = () => {
          const elapsed = Date.now() - startTime
          const progress = Math.min(elapsed / duration, 1)

          // Ease-out cubic
          const eased = 1 - Math.pow(1 - progress, 3)

          setCount(Math.round(tech.years * eased))
          setProgressWidth(Math.min((tech.years / 10) * 100, 100) * eased)

          if (progress < 1) {
            requestAnimationFrame(animate)
          }
        }

        requestAnimationFrame(animate)
      }, delay)
    }
  }, [isInView, tech.years, index])

  return (
    <div
      className={styles.skillCard}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.1}s`
      }}
    >
      <div className={styles.skillInfo}>
        <div className={styles.skillNameRow}>
          {tech.logo && (
            <img
              src={tech.logo}
              alt={tech.name}
              className={styles.skillLogo}
            />
          )}
          <span className={styles.skillName}>{tech.name}</span>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      </div>
      <div className={styles.skillYears}>
        <span className={styles.yearsNumber}>{count}</span>
        <span className={styles.yearsLabel}>YEARS</span>
      </div>
    </div>
  )
}

const ProfessionalSkillsSection = () => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation()
  const { ref: sectionRef, isInView } = useScrollProgress()

  const featuredTech = getFeaturedTechnologies()

  return (
    <section id="skills" className={styles.skills} ref={sectionRef}>
      {/* Decorative elements */}
      <div className={styles.decorativeOrb} style={{ opacity: isInView ? 0.5 : 0 }} />
      <div className={styles.decorativeLines} style={{ opacity: isInView ? 0.3 : 0 }} />

      <div className={styles.container}>
        <div
          ref={headerRef}
          className={styles.header}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <h2 className={styles.sectionTitle}>Technical Expertise</h2>
          <p className={styles.sectionDescription}>
            A deep dive into the systems and tools I use to build scalable products.
          </p>
          <div className={styles.titleUnderline}></div>
        </div>

        <div className={styles.content}>
          <div className={styles.skillsGrid}>
            {featuredTech.map((tech, index) => (
              <SkillCard
                key={index}
                tech={tech}
                index={index}
                isInView={isInView}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProfessionalSkillsSection
